import { useEffect, useId, useState, type ReactElement } from 'react';
import SchedulingConflictTimeGrid, { type SuggestedRange } from './SchedulingConflictTimeGrid';

export type SchedulingConflictReason = 'NO_SLOTS_LEFT' | 'CANNOT_MAKE_OFFERED_TIME';

export interface SchedulingConflictSubmit {
  building: boolean;
  unitId: string;
  propertyId: string | null;
  reason: SchedulingConflictReason;
  reasonNote: string;
  suggestions: SuggestedRange[];
}

interface SchedulingConflictDialogProps {
  homeLabel: string;
  building: boolean;
  unitId: string;
  propertyId: string | null;
  reason: SchedulingConflictReason;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (input: SchedulingConflictSubmit) => void;
}

const NOTE_LIMIT = 2000;

export default function SchedulingConflictDialog({
  homeLabel,
  building,
  unitId,
  propertyId,
  reason,
  busy,
  error,
  onClose,
  onSubmit,
}: SchedulingConflictDialogProps): ReactElement {
  const titleId = useId();
  const noteId = useId();
  const [note, setNote] = useState('');
  const [suggestions, setSuggestions] = useState<SuggestedRange[]>([]);

  useEffect(() => {
    setNote('');
    setSuggestions([]);
  }, [reason, unitId, propertyId]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !busy) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const trimmed = note.trim();
  const noteRequired = reason === 'CANNOT_MAKE_OFFERED_TIME';
  const noSlotsLeft = reason === 'NO_SLOTS_LEFT';
  const canSubmit = suggestions.length > 0 && (!noteRequired || trimmed.length > 0) && !busy;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-4 sm:items-center">
      <button
        type="button"
        aria-label="Close"
        disabled={busy}
        className="absolute inset-0 bg-gray-900/40"
        onClick={onClose}
      />
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative max-h-[92dvh] w-full max-w-lg overflow-y-auto rounded-2xl border border-gray-200 bg-white p-5 shadow-bento dark:border-white/10 dark:bg-gray-800 sm:p-6"
        onSubmit={(event) => {
          event.preventDefault();
          if (!canSubmit) return;
          onSubmit({
            building,
            unitId,
            propertyId,
            reason,
            reasonNote: trimmed,
            suggestions,
          });
        }}
      >
        <h2 id={titleId} className="text-lg font-bold text-gray-900 dark:text-white">
          {noSlotsLeft ? 'No slots left?' : "Can't make these times?"}
        </h2>
        <p className="mt-1 truncate text-sm text-gray-600 dark:text-gray-300">{homeLabel}</p>
        {noSlotsLeft ? (
          <div
            className="mt-3 rounded-xl border border-amber-200/90 bg-amber-50 px-3 py-2.5 dark:border-amber-500/40 dark:bg-amber-950"
            role="note"
          >
            <p className="text-center text-sm font-medium leading-snug text-amber-950 dark:text-amber-100">
              <span aria-hidden="true">⚠️ </span>
              We can&apos;t fit any more tours today. Tell us when you could visit. We&apos;ll use those times when we
              plan the next tour.
              <span aria-hidden="true"> ⚠️</span>
            </p>
          </div>
        ) : null}

        <label htmlFor={noteId} className="mt-4 block text-sm font-medium text-gray-900 dark:text-white">
          {noSlotsLeft ? 'Anything else we should know? (optional)' : "Why can't this time work?"}
        </label>
        <textarea
          id={noteId}
          rows={3}
          maxLength={NOTE_LIMIT}
          required={noteRequired}
          disabled={busy}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder={noteRequired ? 'For example, I have class then.' : 'Anything else we should know.'}
          className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-white/15 dark:bg-gray-900 dark:text-white"
        />

        <div className="mt-4">
          <SchedulingConflictTimeGrid
            suggestions={suggestions}
            disabled={busy}
            heading={noSlotsLeft ? 'When you could tour' : 'Times that could work'}
            onChange={setSuggestions}
          />
        </div>

        {error ? <p className="mt-3 text-sm text-rose-700 dark:text-rose-300">{error}</p> : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={onClose}
            className="rounded-lg px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-60 dark:text-gray-200 dark:hover:bg-white/10"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
          >
            {busy ? 'Requesting…' : 'Request a tour'}
          </button>
        </div>
      </form>
    </div>
  );
}
